import { ChangeEventHandler } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { pathOr, stringToPath } from 'remeda';

import Select from '~/components/ui/Select';
import TextInput from '~/components/ui/TextInput';
import { isMaxLength } from '~/utils/common';

type Option = {
  label: string;
  value: string;
};

type FormTextFieldProps = {
  name: string;
  label?: string;
  placeholder?: string;
  select?: boolean;
  options?: Option[];
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  helperText?: string;
  inputProps?: {
    maxLength?: number;
    [key: string]: any;
  };
  [key: string]: any; // for additional props like 'fullWidth', 'required', etc.
};

export function FormTextField({ name, select = false, options = [], ...props }: FormTextFieldProps) {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange, onBlur }, formState }) => {
        const error = pathOr(formState.errors, stringToPath(name))?.message;
        const maxLengthHelperText = props.inputProps?.maxLength
          ? isMaxLength(value, props.inputProps.maxLength)
          : undefined;

        if (select) {
          return (
            <Select
              items={options}
              {...props}
              value={value}
              onChange={(...val) => {
                onChange(...val);
                props.onChange?.(...val);
              }}
              error={!!error}
              helperText={error}
              onBlur={onBlur}
            />
          );
        }

        return (
          <TextInput
            {...props}
            value={value}
            onChange={(...val) => {
              onChange(...val);
              props.onChange?.(...val);
            }}
            error={!!error}
            helperText={error || props.helperText || maxLengthHelperText}
            onBlur={onBlur}
          />
        );
      }}
    />
  );
}
